import { useState, useEffect } from 'react';
import { Plus, MoreHorizontal, Pencil, Globe, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import type { BlogPostListItem, BlogCategory, PostStatus } from '../../../types';

interface PostsListViewProps {
  posts: BlogPostListItem[];
  categories: BlogCategory[];
  totalPosts: number;
  page: number;
  loading: boolean;
  onCreatePost: () => void;
  onEditPost: (id: string) => void;
  onDeletePost: (id: string) => void;
  onPublishPost: (id: string) => void;
  onFilterChange: (status?: string, search?: string, page?: number) => void;
}

const statusColor: Record<PostStatus, string> = {
  DRAFT: 'bg-gray-500/10 text-gray-500',
  REVIEW: 'bg-yellow-500/10 text-yellow-500',
  SCHEDULED: 'bg-blue-500/10 text-blue-500',
  PUBLISHED: 'bg-green-500/10 text-green-500',
  ARCHIVED: 'bg-red-500/10 text-red-500',
};

const statusLabel: Record<PostStatus, string> = {
  DRAFT: 'Rascunho',
  REVIEW: 'Em Revisão',
  SCHEDULED: 'Agendado',
  PUBLISHED: 'Publicado',
  ARCHIVED: 'Arquivado',
};

const PAGE_SIZE = 20;

export function PostsListView({
  posts,
  categories: _categories,
  totalPosts,
  page,
  loading,
  onCreatePost,
  onEditPost,
  onDeletePost,
  onPublishPost,
  onFilterChange,
}: PostsListViewProps) {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');

  const totalPages = Math.ceil(totalPosts / PAGE_SIZE);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onFilterChange(statusFilter || undefined, search || undefined, 0);
    }, 300);
    return () => clearTimeout(timeout);
  }, [statusFilter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = (value: string) => {
    setStatusFilter(value === 'ALL' ? '' : value);
  };

  const handlePrevPage = () => {
    onFilterChange(statusFilter || undefined, search || undefined, page - 1);
  };

  const handleNextPage = () => {
    onFilterChange(statusFilter || undefined, search || undefined, page + 1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Input
            placeholder="Buscar posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs"
          />
          <Select
            value={statusFilter || 'ALL'}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="DRAFT">Rascunho</SelectItem>
              <SelectItem value="REVIEW">Em Revisão</SelectItem>
              <SelectItem value="SCHEDULED">Agendado</SelectItem>
              <SelectItem value="PUBLISHED">Publicado</SelectItem>
              <SelectItem value="ARCHIVED">Arquivado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={onCreatePost}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Post
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">Título</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Autor</TableHead>
              <TableHead>Atualizado</TableHead>
              <TableHead className="text-right">Views</TableHead>
              <TableHead className="w-[60px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Carregando...
                </TableCell>
              </TableRow>
            ) : posts.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-8 text-muted-foreground"
                >
                  Nenhum post encontrado
                </TableCell>
              </TableRow>
            ) : (
              posts.map((post) => (
                <TableRow
                  key={post.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onEditPost(post.id)}
                >
                  {/* Title + thumbnail */}
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {post.coverImageUrl && (
                        <img
                          src={post.coverImageUrl}
                          alt=""
                          className="h-10 w-14 rounded object-cover flex-shrink-0"
                        />
                      )}
                      <span className="font-medium truncate max-w-[200px]">
                        {post.title}
                      </span>
                    </div>
                  </TableCell>

                  {/* Categories */}
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {post.categories.map((cat) => (
                        <Badge
                          key={cat.id}
                          variant="outline"
                          style={{ borderColor: cat.color, color: cat.color }}
                        >
                          {cat.name}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Badge
                      className={cn(
                        statusColor[post.status as PostStatus] ??
                          'bg-gray-500/10 text-gray-500'
                      )}
                    >
                      {statusLabel[post.status as PostStatus] ?? post.status}
                    </Badge>
                  </TableCell>

                  {/* Author */}
                  <TableCell className="text-sm text-muted-foreground">
                    {post.authorName}
                  </TableCell>

                  {/* Updated at */}
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(post.updatedAt).toLocaleDateString('pt-BR')}
                  </TableCell>

                  {/* View count */}
                  <TableCell className="text-right text-sm">
                    {post.viewCount.toLocaleString('pt-BR')}
                  </TableCell>

                  {/* Actions dropdown */}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Abrir menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditPost(post.id);
                          }}
                        >
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                        {post.status !== 'PUBLISHED' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              onPublishPost(post.id);
                            }}
                          >
                            <Globe className="h-4 w-4 mr-2" />
                            Publicar
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeletePost(post.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {totalPosts.toLocaleString('pt-BR')} posts encontrados
          </span>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={handlePrevPage}
            >
              Anterior
            </Button>
            <span className="flex items-center text-sm px-3">
              {page + 1} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={handleNextPage}
            >
              Próximo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
